import { AnimatePresence, motion } from 'framer-motion';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { CopyNumberVariantsContent } from './CopyNumberVariantsContent';
import { defineMessages, useIntl } from 'react-intl';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { ExpressionsContent } from './ExpressionsContent';
import { Helix } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { SavedFiltersControls } from './SavedFilters';
import { ShortVariantsContent } from './ShortVariantsContent';
import { StructuralVariantsContent } from './StructuralVariantsContent';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useActiveVariantType } from '../ActiveVariantTypeContext';
import { useHashParams } from '@lifeomic/phc-web-toolkit/src/hooks/useHashParams';
import { UseHydrateOmicsExplorer } from '../useHydrateOmicsExplorer';
import { useSelectedFilterOptions } from '../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import { useVariantCount } from '../VariantCountContext';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { VariantDetails } from '../VariantDetails';
import { OEHeader } from './OEHeader';
import { buildFetchVariantSelectionColumn } from './VariantSelectionColumn';
import {
  ContentContainer,
  FilterContentLayoutManager,
  FilterContainer,
} from '@lifeomic/phc-web-toolkit/src/components/FilterContent';
import {
  copyNumberOptions as copyNumberFilters,
  shortOptions as shortVariantFilters,
  structuralVariantOptions as structuralFilters,
  expressionOptions as expressionFilters,
} from './options';
import {
  getAAChangeTextOnly,
  getGeneTextOnly as getShortGeneTextOnly,
  getNNChange,
  getVariantClassTextOnly,
} from './ShortVariantsContent/buildTableData';
import {
  getEffect as getCopyNumberEffect,
  getGeneTextOnly as getCopyNumberGeneTextOnly,
} from './CopyNumberVariantsContent/buildTableData';
import {
  getEffect as getStructuralEffect,
  getGeneTextOnly as getStructuralTextOnly,
} from './StructuralVariantsContent/buildTableData';
import {
  getExpression,
  getGeneTextOnly as getExpressionGeneTextOnly,
} from './ExpressionsContent/buildTableData';
import * as React from 'react';
import { handleFilterAdd, handleFilterRemove } from './utils';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import mixpanel from 'mixpanel-browser';
import {
  useActiveAccountId,
  useActiveProjectId,
  useFeatures,
  useAccountProducts,
  useActiveAccount,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { Events } from '../../../../src/util/mixpanel/Events';
import DiagnosticReport = fhir.DiagnosticReport;
import { SelectedVariantsContext } from '../SelectedVariantsContext';
import { SequenceType, Test } from '../types';

const messages = defineMessages({
  variantCountLabel: {
    id: 'omicsExplorer.filterResultsManager.variantCountLabel',
    defaultMessage: 'Variant Count',
  },
  variantCountLarge: {
    id: 'omicsExplorer.filterResultsManager.variantCountLarge',
    defaultMessage: '999+',
  },
  shortVariantTitle: {
    id: 'omicsExplorer.filterResultsManager.modalTitle.shortVariantTitle',
    defaultMessage: '{gene} {aa} {nn} {class}',
  },
  geneEffectTitle: {
    id: 'omicsExplorer.filterResultsManager.modalTitle.geneEffectTitle',
    defaultMessage: '{gene} {effect}',
  },
  geneExpressionTitle: {
    id: 'omicsExplorer.filterResultsManager.modalTitle.geneExpressionTitle',
    defaultMessage: '{gene} {expression}',
  },
});

const useStyles = makeStyles(theme => ({
  filterHeaderText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  filterFooterIcon: {
    marginRight: theme.spacing(1),
    stroke: theme.palette.purple.main,
  },
  filterFooterVariantCountText: {
    color: theme.palette.purple.main,
  },
  savedFiltersContainerOverride: {
    display: 'flex',
    flexDirection: 'column',
    height: '7.5rem', // Safari requires a set height
    paddingBottom: 0,
    width: '100%',
  },
  contentContainer: {
    background: theme.palette.black[50],
    maxHeight: '100%',
    maxWidth: '100%',
    padding: theme.spacing(2),
    '@media (max-height: 800px)': {
      padding: theme.spacing(0.5),
    },
  },
  headerOverrides: {
    minHeight: 'unset',
  },
  resultsContentBox: {
    maxWidth: '100%',
    maxHeight: '100%',
    overflowX: 'auto',
    boxShadow: theme.boxShadows.table,
  },
  modalContent: {
    padding: theme.spacing(1.5, 4, 4),
  },
  checkboxClass: {
    padding: theme.spacing(1.5),
    margin: `calc(-1 * ${theme.spacing(1.5)})`,
  },
}));

interface FilterResultsManagerProps {
  layoutClassName?: string;
  selectedSequenceType: SequenceType;
  state?: UseHydrateOmicsExplorer['state'];
  activeTest: Test;
  subjectId?: string;
}

export const FilterResultsManager: React.FC<FilterResultsManagerProps> = ({
  layoutClassName,
  state,
  activeTest,
  selectedSequenceType,
  subjectId,
}) => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();

  const activeAccount = useActiveAccount();
  const isFeatureEnabled = useFeatures();
  const hasAccountProduct = useAccountProducts();
  const isMTBNotesEnabled = hasAccountProduct('MTBNotes');
  const isFountainLifeAccount = !!activeAccount?.id?.startsWith('fountainlife');
  const isVariantSelectionEnabled = isMTBNotesEnabled || isFountainLifeAccount;
  const isFilterSmallDisplayEnabled = isFeatureEnabled('filterSmallDisplay');
  const aminoAcidField = isFountainLifeAccount
    ? 'hgvsAminoAcidChange'
    : 'aminoAcidChange';

  const [, onHashParamPush] = useHashParams();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();

  const [selectedActiveVariantType] = useActiveVariantType();
  const [variantCount] = useVariantCount();
  const [selectedFilterOptions, setSelectedFilterOptions] =
    useSelectedFilterOptions();
  const {
    createTargetedVariant,
    variantSelection,
    updateVariantSelection,
    refreshTargetedVariants,
    error,
  } = React.useContext(SelectedVariantsContext);

  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    data: any;
    title: string;
  }>({
    isOpen: false,
    data: null,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    title: null,
  });

  const [disableSelectionColumn, setDisableSelectionColumn] =
    // For FL, we want to always allow selecting variants to support auto
    // population of the Hereditary Disease Risk Screen Report.
    React.useState<boolean>(!isFountainLifeAccount);

  const currentFilterOptions =
    selectedActiveVariantType === 'short'
      ? shortVariantFilters
      : selectedActiveVariantType === 'structural'
      ? structuralFilters
      : selectedActiveVariantType === 'copyNumber'
      ? copyNumberFilters
      : expressionFilters;

  const handleRowClick = (row: any) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let title: string = null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let geneName: string = null;

    switch (selectedActiveVariantType) {
      case 'short':
        geneName = getShortGeneTextOnly(row);
        title = formatMessage(messages.shortVariantTitle, {
          gene: geneName,
          aa: getAAChangeTextOnly(row, aminoAcidField),
          nn: getNNChange(row),
          class: getVariantClassTextOnly(row),
        });

        break;
      case 'copyNumber':
        geneName = getCopyNumberGeneTextOnly(row);
        title = formatMessage(messages.geneEffectTitle, {
          gene: geneName,
          effect: getCopyNumberEffect(row),
        });
        break;
      case 'expression':
        geneName = getExpressionGeneTextOnly(row);
        title = formatMessage(messages.geneExpressionTitle, {
          gene: geneName,
          expression: getExpression(row),
        });
        break;
      case 'structural':
        geneName = getStructuralTextOnly(row);
        title = formatMessage(messages.geneEffectTitle, {
          gene: geneName,
          effect: getStructuralEffect(row),
        });
        break;
      default:
        break;
    }

    mixpanel.track(Events.omicsExplorer.variantRowClick, {
      accountId,
      gene: geneName,
      projectId,
      variantType: selectedActiveVariantType,
    });

    setDialogState({ isOpen: true, data: row, title });
  };

  const handleDialogClose = () =>
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setDialogState({ isOpen: false, data: null, title: null });

  const fetchVariantSelectionColumn = buildFetchVariantSelectionColumn({
    activeAccount: activeAccount?.id,
    formatMessage,
    variantSelection,
    updateVariantSelection,
    disabled: disableSelectionColumn,
    cellClassName: classes.checkboxClass,
  });

  const selectDiagnosticReport = React.useCallback(
    async (diagnosticReport: DiagnosticReport) => {
      // disable selection column when report status is final
      setDisableSelectionColumn(diagnosticReport.status === 'final');
      return await refreshTargetedVariants(diagnosticReport);
    },
    [refreshTargetedVariants],
  );

  React.useEffect(() => {
    setDisableSelectionColumn(prev => {
      if (prev) {
        return prev;
      }
      return !!error;
    });
  }, [error]);

  const fetchPrependedColumns = isVariantSelectionEnabled
    ? // For FL, we only show variant selection when filters are applied to keep
      // the UX cleaner.
      isFountainLifeAccount
      ? selectedFilterOptions.length
        ? fetchVariantSelectionColumn
        : null
      : fetchVariantSelectionColumn
    : null;

  const VariantsComponent =
    selectedActiveVariantType === 'short'
      ? ShortVariantsContent
      : selectedActiveVariantType === 'structural'
      ? StructuralVariantsContent
      : selectedActiveVariantType === 'copyNumber'
      ? CopyNumberVariantsContent
      : selectedActiveVariantType === 'expression'
      ? ExpressionsContent
      : null;

  return (
    <>
      <Modal
        isFullScreen={true}
        title={dialogState.title}
        isOpen={dialogState.isOpen}
        onDismiss={handleDialogClose}
        contentClassName={classes.modalContent}
      >
        <VariantDetails
          data={dialogState.data}
          variantType={selectedActiveVariantType}
        />
      </Modal>
      <FilterContentLayoutManager
        className={layoutClassName}
        filter={
          <FilterContainer
            headerClassName={classes.savedFiltersContainerOverride}
            header={<SavedFiltersControls state={state} />}
            footer={
              <>
                <Helix className={classes.filterFooterIcon} />
                <Box justify="space-between" fullWidth>
                  <Text
                    className={classes.filterFooterVariantCountText}
                    size="subbody"
                  >
                    {formatMessage(messages.variantCountLabel)}
                  </Text>
                  <AnimatePresence>
                    {Number.isFinite(variantCount) && (
                      <Text size="subbody" weight="bold">
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { duration: 0.5 },
                          }}
                          exit={{
                            opacity: 0,
                            transition: { duration: 0.25 },
                          }}
                        >
                          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                          {variantCount > 999
                            ? formatMessage(messages.variantCountLarge)
                            : variantCount}
                        </motion.span>
                      </Text>
                    )}
                  </AnimatePresence>
                </Box>
              </>
            }
          >
            {currentFilterOptions?.map((option, index) => {
              const { content: FilterContent, id, title, ...props } = option;

              return (
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                <ExpansionPanel key={index} title={formatMessage(title)}>
                  {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                  <FilterContent
                    {...props}
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    origin={formatMessage(title)}
                    onFilterAdd={(option: any) =>
                      handleFilterAdd({
                        onHashParamPush,
                        option,
                        selectedFilterOptions,
                        setSelectedFilterOptions,
                      })
                    }
                    onFilterRemove={(option: any) =>
                      handleFilterRemove({
                        onHashParamPush,
                        option,
                        selectedFilterOptions,
                        setSelectedFilterOptions,
                      })
                    }
                    selectedFilterOptions={selectedFilterOptions}
                  />
                </ExpansionPanel>
              );
            })}
          </FilterContainer>
        }
      >
        <ContentContainer
          contentClass={classes.contentContainer}
          headerOverrides={classes.headerOverrides}
          header={
            <OEHeader
              activeTest={activeTest}
              selectedSequenceType={selectedSequenceType}
              selectedFilterOptions={selectedFilterOptions}
              setSelectedFilterOptions={setSelectedFilterOptions}
              selectDiagnosticReport={selectDiagnosticReport}
              variantSelection={variantSelection}
              createTargetedVariant={createTargetedVariant}
              isVariantSelectionEnabled={isVariantSelectionEnabled}
              isMTBNotesEnabled={isMTBNotesEnabled}
              isFilterSmallDisplayEnabled={isFilterSmallDisplayEnabled}
              selectedVariantError={error}
              subjectId={subjectId}
            />
          }
        >
          {state === 'COMPLETE' && (
            <div className={classes.resultsContentBox}>
              {VariantsComponent && (
                <VariantsComponent
                  onRowClick={handleRowClick}
                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  fetchPrependedColumns={fetchPrependedColumns}
                />
              )}
            </div>
          )}
        </ContentContainer>
      </FilterContentLayoutManager>
    </>
  );
};
